import { AdminType, APIDelete, APIGet, APIPost, APIPut, APIUrl, IAPIResponse, StatusCode } from "api/protocols";
import { CancelToken } from "axios";
import { ICart } from "redux/reducers/models/cart";

export type TableDegreeRotation = 0 | 45 | 90 | 135 | 180 | 225 | 270 | 315;

export interface ITable {
  facility_id: number;
  floor_plan_id?: number;
  template_id?: number;
  id: number;
  open: number;
  open_time?: string;
  register_group_id: number;
  rotation: TableDegreeRotation;
  seats: number;
  short_title: string;
  table_element_id: number;
  table_section_id: number;
  title: string;
  user_full_name?: string;
  user_id?: number;
  x_coordinate: number;
  y_coordinate: number;
  carts?: Array<ICart>;
}

export interface IFloorPlanTable {
  id: string;
  x_coordinate: number;
  y_coordinate: number;
  seats: number;
  title: string;
  short_title: string;
  table_element_id: number;
  floor_plan_id?: number;
  template_id?: number;
  rotation: TableDegreeRotation;
  open: boolean;
}

interface IGetTable {
  id?: number;
  template_id?: number;
  carts?: boolean;
  register_group_id?: number;
  orders?: boolean;
  floor_plan_id?: number;
}

interface IGetTableResponse extends IAPIResponse {
  data: ITable[];
}

interface IPostTable {
  id?: number;
  title?: string;
  table_section_id?: number;
  register_group_id?: number;
  tables?: Omit<IFloorPlanTable, "id">[];
}

interface IPostTableResponse {
  status: StatusCode;
  data: ITable[];
}

interface IPutTable {
  id?: number;
  title?: string;
  table_section_id?: number;
  register_group_id?: number;
  tables?: IFloorPlanTable[];
  user_id?: number;
}

interface IPutTableResponse extends IAPIResponse {
  data: ITable[];
}

interface IPutArchiveTable {
  id?: number;
  table_ids?: number[];
}

interface IPutArchiveTableResponse extends IAPIResponse {
  data: ITable[];
}

interface IGetTableReceipt {
  table_id: number;
  line_item_ids: Array<number>;
}

interface IGetTableReceiptResponse extends IAPIResponse {
  data: string;
}

interface IPutOpenTable {
  table_id: number;
  number_of_seats: number;
  register_id: number;
  reservation_customer_id?: number;
}

interface IPutOpenTableResponse extends IAPIResponse {
  data: ITable;
}

interface IPutCloseTable {
  table_id: number;
}

interface IPutCloseTableResponse extends IAPIResponse {
  data: ITable;
}

export interface ITableElement {
  id: number;
  title: string;
  seats: number;
  image_src: string;
  grid_width: number;
  grid_height: number;
}

interface IGetTableElementsResponse extends IAPIResponse {
  data: ITableElement[];
}

export interface IFloorPlan extends KitchenLocationMeta {
  id: number;
  facility_id: number;
  title: string;
  register_group_id: number;
  grid_width: number;
  grid_height: number;
  display_seat_numbers: number;
}

interface IGetFloorPlans {
  id?: number;
  register_group_id?: number;
}

interface IPostFloorPlansResponse {
  data: IFloorPlan[];
}

interface IPutFloorPlan extends KitchenLocationMeta {
  id: number;
  title: string;
  grid_width: number;
  grid_height: number;
  register_group_id: number;
  display_seat_numbers: boolean;
}

interface IPutFloorPlansResponse extends IAPIResponse {
  data: IFloorPlan[];
}

interface IDeleteFloorPlan {
  id: number;
}

interface IPutMoveSeats {
  table_id: number;
  cart_ids: Array<number>;
  register_id?: number;
}

interface IMoveSeatsData {
  carts: Array<ICart>;
  facility_id: number;
  floor_plan_id: number;
  id: number;
  open: boolean;
  open_time: string;
  register_group_id: number;
  rotation: number;
  seats: number;
  short_title: string;
  table_element_id: number;
  table_section_id: number;
  title: string;
  token: string;
  user_full_name: string;
  user_id: number;
  x_coordinate: number;
  y_coordinate: number;
}

interface IPutMoveSeatsResponse extends IAPIResponse {
  data: IMoveSeatsData;
}

interface IPostFloorPlan {
  title: string;
  grid_width?: number;
  grid_height?: number;
}

export type KitchenLocationMeta = {
  kitchen_location_id_1: number;
  kitchen_location_id_2: number;
  kitchen_location_id_3: number;
};

export async function GetTable(
  params: IGetTable,
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
): Promise<IGetTableResponse> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/table"), params, useGlobalLoader, cancelToken);
}

export interface IGetQuickSaleTableResponse extends IAPIResponse {
  data: ITable;
}

export async function GetQuickSaleTable(
  params: IGetTable,
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
): Promise<IGetQuickSaleTableResponse> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/table/quick"), params, useGlobalLoader, cancelToken);
}

export async function PostTable(params: IPostTable, useGlobalLoader: boolean): Promise<IPostTableResponse> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/facility/table"), params, useGlobalLoader);
}

export async function PutTable(params: IPutTable, useGlobalLoader: boolean): Promise<IPutTableResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table"), params, useGlobalLoader);
}

export async function PutArchiveTable(
  params: IPutArchiveTable,
  useGlobalLoader: boolean,
): Promise<IPutArchiveTableResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table/archive"), params, useGlobalLoader);
}

export async function GetTableReceipt(
  params: IGetTableReceipt,
  useGlobalLoader: boolean,
): Promise<IGetTableReceiptResponse> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/table/receipt"), params, useGlobalLoader);
}

export async function PutOpenTable(params: IPutOpenTable, useGlobalLoader: boolean): Promise<IPutOpenTableResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table/open"), params, useGlobalLoader);
}

export async function PutCloseTable(params: IPutCloseTable, useGlobalLoader: boolean): Promise<IPutCloseTableResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table/close"), params, useGlobalLoader);
}

export async function GetTableElements(
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IGetTableElementsResponse> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/table/element"), {}, useGlobalLoader, token);
}

export async function GetFloorPlans(params: IGetFloorPlans, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IFloorPlan[]>(
    APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostFloorPlan(
  params: IPostFloorPlan,
  useGlobalLoader: boolean,
): Promise<IPostFloorPlansResponse> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan"), params, useGlobalLoader);
}

export async function PutFloorPlan(params: IPutFloorPlan, useGlobalLoader: boolean): Promise<IPutFloorPlansResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan"), params, useGlobalLoader);
}

export async function DeleteFloorPlan(params: IDeleteFloorPlan, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan"), params, useGlobalLoader);
}

export async function PutMoveSeats(params: IPutMoveSeats, useGlobalLoader: boolean): Promise<IPutMoveSeatsResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table/move"), params, useGlobalLoader);
}

// Floor Plan Templates

export interface IFloorPlanTemplate {
  id: number;
  // facility_id: number;
  title: string;
  grid_width: number;
  grid_height: number;
}
export interface IGetFloorPlantTemplates {
  id?: number;
}

export interface IPostFloorPlanTemplate {
  title: string;
}

export interface IPutFloorPlanTemplate {
  id: number;
  title?: string;
  grid_width?: number;
  grid_height?: number;
}

export interface IDeleteFloorPlanTemplate {
  id: number;
}

export interface IGetFloorPlanTemplateResponse extends IAPIResponse {
  data: IFloorPlanTemplate[];
}

export interface IPostFloorPlanTemplateResponse extends IAPIResponse {
  data: IFloorPlanTemplate;
}

export interface IPutFloorPlanTemplateResponse extends IAPIResponse {
  data: IFloorPlanTemplate;
}

// interface IGetTableElementsResponse extends IAPIResponse {
//   data: ITableElement[];
//   status: StatusCode;
// }
export async function GetFloorPlanTemplate(
  params: IGetFloorPlantTemplates,
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
): Promise<IGetFloorPlanTemplateResponse> {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan/template"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function PostFloorPlanTemplate(
  params: IPostFloorPlanTemplate,
  useGlobalLoader: boolean,
): Promise<IPostFloorPlanTemplateResponse> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan/template"), params, useGlobalLoader);
}

export async function PutFloorPlanTemplate(
  params: IPutFloorPlanTemplate,
  useGlobalLoader: boolean,
): Promise<IPutFloorPlanTemplateResponse> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan/template"), params, useGlobalLoader);
}

export async function DeleteFloorPlanTemplate(params: IDeleteFloorPlanTemplate, useGlobalLoader: boolean) {
  return await APIDelete(
    APIUrl(AdminType.FacilityAdmin, "/facility/table/floor-plan/template"),
    params,
    useGlobalLoader,
  );
}
